import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SignupStep } from "./SignupStep";
import {
  Building,
  Hash,
  MapPin,
  Globe,
  Info,
  Mail,
  Search,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  COMPANY_COUNTRIES,
  COMPANY_LOOKUP_SOURCE_LABEL,
  companyLookupApi,
  isLookupSupported,
  isValidOrgNumberFormat,
  type CompanyLookupSource,
} from "@/lib/api/company-lookup";

const emailCheck = z.email();

const companyInfoSchema = z
  .object({
    companyName: z
      .string()
      .max(255, "Company name must not exceed 255 characters")
      .optional()
      .or(z.literal("")),
    billingEmail: z
      .string()
      .max(255, "Billing email must not exceed 255 characters")
      .refine(
        val => !val || emailCheck.safeParse(val).success,
        "Please enter a valid billing email address"
      )
      .optional()
      .or(z.literal("")),
    cvrNumber: z
      .string()
      .max(50, "CVR/Org number must not exceed 50 characters")
      .regex(
        /^[0-9A-Z\s-]*$/,
        "CVR/Org number can only contain numbers, letters, spaces, and hyphens"
      )
      .optional()
      .or(z.literal("")),
    address: z
      .string()
      .max(500, "Address must not exceed 500 characters")
      .optional()
      .or(z.literal("")),
    postalCode: z
      .string()
      .max(20, "Postal code must not exceed 20 characters")
      .regex(/^[0-9A-Z\s-]*$/, "Postal code can only contain numbers, letters, spaces, and hyphens")
      .optional()
      .or(z.literal("")),
    city: z
      .string()
      .max(100, "City must not exceed 100 characters")
      .regex(
        /^[a-zA-ZÀ-ÿ\s'-]*$/,
        "City can only contain letters, spaces, hyphens, and apostrophes"
      )
      .optional()
      .or(z.literal("")),
    country: z.string().optional().or(z.literal("")),
    website: z
      .string()
      .transform(val => {
        if (val && !val.match(/^https?:\/\//)) {
          return `https://${val}`;
        }
        return val;
      })
      .refine(
        val =>
          !val ||
          (() => {
            try {
              new URL(val);
              return true;
            } catch {
              return false;
            }
          })(),
        "Please enter a valid website URL"
      )
      .refine(val => !val || val.length <= 255, "Website URL must not exceed 255 characters")
      .optional()
      .or(z.literal("")),
  })
  .superRefine((data, ctx) => {
    // Self-billing invoices are emailed to the supplier company (#795) — a
    // company without a billing address dead-ends at invoice send (BE #734).
    if (data.companyName && !data.billingEmail) {
      ctx.addIssue({
        code: "custom",
        path: ["billingEmail"],
        message: "A billing email is required when adding a company",
      });
    }
  });

type CompanyInfoFormData = z.infer<typeof companyInfoSchema>;

interface CompanyInfoStepProps {
  formData: {
    companyName: string;
    billingEmail: string;
    cvrNumber: string;
    address: string;
    postalCode: string;
    city: string;
    country: string;
    website: string;
  };
  /** Applicant's own email (step 1) — one-time prefill for the billing email. */
  applicantEmail?: string;
  isLoading: boolean;
  error?: string;
  onFormChange: (data: Partial<CompanyInfoFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export const CompanyInfoStep: React.FC<CompanyInfoStepProps> = ({
  formData,
  applicantEmail,
  isLoading,
  error,
  onFormChange,
  onNext,
  onBack,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<CompanyInfoFormData>({
    resolver: zodResolver(companyInfoSchema),
    // One-time prefill: most one-person consultancies bill from the address
    // they sign up with; editable, and only enforced when a company is named.
    defaultValues: { ...formData, billingEmail: formData.billingEmail || applicantEmail || "" },
    mode: "onChange",
  });

  // Watch form changes and update parent state
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch() is not React Compiler-compatible
    const subscription = watch((value: Partial<CompanyInfoFormData>) => {
      onFormChange(value as Partial<CompanyInfoFormData>);
    });
    return () => subscription.unsubscribe();
  }, [watch, onFormChange]);

  const normaliseWebsite = () => {
    const val = watch("website")?.trim() ?? "";
    if (val && !/^https?:\/\//i.test(val)) {
      setValue("website", `https://${val}`, { shouldValidate: true });
    }
  };

  const onSubmit = () => {
    normaliseWebsite();
    onNext();
  };

  const handleCountryChange = (value: string) => {
    setValue("country", value, { shouldValidate: true });
    setLookupResult(null);
  };

  const selectedCountry = watch("country");
  const cvrNumber = watch("cvrNumber") ?? "";

  const [isLookingUp, setIsLookingUp] = React.useState(false);
  const [lookupResult, setLookupResult] = React.useState<
    | { kind: "found"; source: CompanyLookupSource }
    | { kind: "not-found" }
    | { kind: "error" }
    | null
  >(null);

  const lookupSupported = isLookupSupported(selectedCountry ?? "");
  const orgNumberValid =
    lookupSupported && isValidOrgNumberFormat(selectedCountry ?? "", cvrNumber);
  const canLookup = lookupSupported && orgNumberValid && !isLookingUp && !isLoading;

  const cvrPlaceholder = (() => {
    switch (selectedCountry) {
      case "DK":
        return "e.g., 12345678 (Denmark CVR)";
      case "NO":
        return "e.g., 123456789 (Norway Org.nr)";
      case "SE":
        return "e.g., 5560000000 (Sweden Org.nr)";
      default:
        return "Enter your business registration number";
    }
  })();

  const handleLookup = async () => {
    if (!canLookup || !selectedCountry) {
      return;
    }
    setIsLookingUp(true);
    setLookupResult(null);
    try {
      const result = await companyLookupApi.lookup(selectedCountry, cvrNumber);
      if (!result) {
        setLookupResult({ kind: "not-found" });
        return;
      }
      setValue("companyName", result.company_name, { shouldValidate: true });
      if (result.address !== undefined) {
        setValue("address", result.address, { shouldValidate: true });
      }
      if (result.postal_code !== undefined) {
        setValue("postalCode", result.postal_code, { shouldValidate: true });
      }
      if (result.city !== undefined) {
        setValue("city", result.city, { shouldValidate: true });
      }
      if (result.website !== undefined && result.website !== "") {
        setValue("website", result.website, { shouldValidate: true });
      }
      setLookupResult({ kind: "found", source: result.source });
    } catch (err) {
      console.error("Company lookup failed:", err);
      setLookupResult({ kind: "error" });
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <SignupStep
      title="Company Information"
      description="Tell us about your company or business (optional). This information helps clients understand your professional setup."
      canGoNext={isValid}
      canGoBack={true}
      isLoading={isLoading}
      onNext={handleSubmit(onSubmit)}
      onBack={onBack}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="companyName" className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            Company Name
          </Label>
          <Input
            id="companyName"
            type="text"
            placeholder="Enter your company name"
            {...register("companyName")}
            className={cn(
              "transition-all duration-200",
              errors.companyName && "border-destructive focus-visible:ring-destructive"
            )}
            disabled={isLoading}
          />
          {errors.companyName && (
            <p className="text-sm text-destructive font-medium">{errors.companyName.message}</p>
          )}
        </div>

        {/* Billing email — self-billing invoices are sent here (#795 / BE #734) */}
        <div className="space-y-2">
          <Label htmlFor="billingEmail" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Billing Email
          </Label>
          <Input
            id="billingEmail"
            type="email"
            placeholder="billing@yourcompany.com"
            {...register("billingEmail")}
            className={cn(
              "transition-all duration-200",
              errors.billingEmail && "border-destructive focus-visible:ring-destructive"
            )}
            disabled={isLoading}
          />
          {errors.billingEmail && (
            <p className="text-sm text-destructive font-medium">{errors.billingEmail.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Self-billing invoices for your work are sent to this address. Pre-filled with your own
            email — change it if your company bills from a different one.
          </p>
        </div>

        {/* Country - Moved before CVR for country-specific organization verification */}
        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <Select value={selectedCountry} onValueChange={handleCountryChange} disabled={isLoading}>
            <SelectTrigger
              id="country"
              className={cn(
                "transition-all duration-200",
                errors.country && "border-destructive focus-visible:ring-destructive"
              )}
            >
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_COUNTRIES.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.country && (
            <p className="text-sm text-destructive font-medium">{errors.country.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Select your country to enable country-specific organization validation
          </p>
        </div>

        {/* CVR/Organization Number */}
        <div className="space-y-2">
          <Label htmlFor="cvrNumber" className="flex items-center gap-2">
            <Hash className="w-4 h-4" />
            CVR/Organization Number
          </Label>
          <div className="flex gap-2">
            <Input
              id="cvrNumber"
              type="text"
              placeholder={cvrPlaceholder}
              {...register("cvrNumber", {
                onChange: () => setLookupResult(null),
              })}
              className={cn(
                "transition-all duration-200",
                errors.cvrNumber && "border-destructive focus-visible:ring-destructive"
              )}
              disabled={isLoading}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleLookup}
              disabled={!canLookup}
              title={
                lookupSupported
                  ? "Look up company in the public registry"
                  : "Lookup available for Denmark, Norway, and Sweden"
              }
              className="shrink-0"
            >
              {isLookingUp ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              <span className="ml-2 hidden sm:inline">
                {isLookingUp ? "Looking up..." : "Look up"}
              </span>
            </Button>
          </div>
          {errors.cvrNumber && (
            <p className="text-sm text-destructive font-medium">{errors.cvrNumber.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Enter your business registration number (CVR in Denmark, Org.nr in Norway/Sweden, etc.)
            {lookupSupported
              ? " — use “Look up” to auto-fill from the public registry."
              : " Lookup is only available for Denmark, Norway, and Sweden — fill the remaining fields manually for other countries."}
          </p>

          {lookupResult?.kind === "found" && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Company found via {COMPANY_LOOKUP_SOURCE_LABEL[lookupResult.source]}. Review the
                auto-filled details below and adjust if needed.
              </AlertDescription>
            </Alert>
          )}
          {lookupResult?.kind === "not-found" && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Company not found in the registry. Please fill in the details manually.
              </AlertDescription>
            </Alert>
          )}
          {lookupResult?.kind === "error" && (
            <Alert variant="destructive">
              <AlertDescription>
                Lookup failed. You can still enter the details manually.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Company Address */}
        <div className="space-y-2">
          <Label htmlFor="address" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Company Address
          </Label>
          <Input
            id="address"
            type="text"
            placeholder="Street address, building number, etc."
            {...register("address")}
            className={cn(
              "transition-all duration-200",
              errors.address && "border-destructive focus-visible:ring-destructive"
            )}
            disabled={isLoading}
          />
          {errors.address && (
            <p className="text-sm text-destructive font-medium">{errors.address.message}</p>
          )}
        </div>

        {/* Postal Code and City */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="postalCode">Postal Code</Label>
            <Input
              id="postalCode"
              type="text"
              placeholder="e.g., 2100"
              {...register("postalCode")}
              className={cn(
                "transition-all duration-200",
                errors.postalCode && "border-destructive focus-visible:ring-destructive"
              )}
              disabled={isLoading}
            />
            {errors.postalCode && (
              <p className="text-sm text-destructive font-medium">{errors.postalCode.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              type="text"
              placeholder="e.g., Copenhagen"
              {...register("city")}
              className={cn(
                "transition-all duration-200",
                errors.city && "border-destructive focus-visible:ring-destructive"
              )}
              disabled={isLoading}
            />
            {errors.city && (
              <p className="text-sm text-destructive font-medium">{errors.city.message}</p>
            )}
          </div>
        </div>

        {/* Company Website */}
        <div className="space-y-2">
          <Label htmlFor="website" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Company Website
          </Label>
          <Input
            id="website"
            type="text"
            placeholder="bondit.dk"
            {...register("website")}
            onBlur={e => {
              const val = e.target.value.trim();
              if (val && !/^https?:\/\//i.test(val)) {
                setValue("website", `https://${val}`, { shouldValidate: true });
              }
            }}
            className={cn(
              "transition-all duration-200",
              errors.website && "border-destructive focus-visible:ring-destructive"
            )}
            disabled={isLoading}
          />
          {errors.website && (
            <p className="text-sm text-destructive font-medium">
              {errors.website.message as string}
            </p>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Info Message */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            All company information is optional. You can skip this step or add company details if
            you operate as a sole proprietorship or business. This information will be shown to
            potential clients when they view your consultant profile.
          </AlertDescription>
        </Alert>
      </form>
    </SignupStep>
  );
};
